import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  Users, 
  DollarSign, 
  FileText, 
  MessageSquare,
  TrendingUp,
  Calendar,
  MapPin,
  Brain,
  Heart,
  Shield,
  Settings,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  BarChart3,
  PieChart,
  Activity,
  Globe,
  Zap,
  Bell,
  Clock,
  Star,
  Flag,
  Upload,
  File,
  BookOpen,
  Plus
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  
  // Dashboard State
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // User Management State
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userFilters, setUserFilters] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Support Tickets State
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  
  // Moderation State
  const [moderationQueue, setModerationQueue] = useState([]);
  
  // Knowledge Base State
  const [knowledgeDocuments, setKnowledgeDocuments] = useState([]);
  const [knowledgeStats, setKnowledgeStats] = useState(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewingDocumentDetails, setViewingDocumentDetails] = useState(false);
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentFilters, setDocumentFilters] = useState({});
  
  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'educational',
    access_level: 'public',
    tags: ''
  });
  
  // Load dashboard data
  useEffect(() => {
    if (isAuthenticated) {
      loadDashboardData();
    }
  }, [isAuthenticated]);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else if (response.status === 403) {
        toast({
          variant: "destructive",
          title: "Access Denied",
          description: "Admin access required to view this page.",
        });
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load admin dashboard.",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: userSearch,
          ...userFilters
        })
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadTickets = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/support/tickets`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data);
      }
    } catch (error) {
      console.error('Failed to load tickets:', error);
    }
  };

  const loadModerationQueue = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/moderation/posts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setModerationQueue(data);
      }
    } catch (error) {
      console.error('Failed to load moderation queue:', error);
    }
  };

  const loadKnowledgeDocuments = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge_base/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setKnowledgeDocuments(data.documents);
      }
    } catch (error) {
      console.error('Failed to load knowledge documents:', error);
    }
  };

  const loadKnowledgeStats = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge_base/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setKnowledgeStats(data);
      }
    } catch (error) {
      console.error('Failed to load knowledge stats:', error);
    }
  };

  // Load data when switching tabs
  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'support') {
      loadTickets();
    } else if (activeTab === 'moderation') {
      loadModerationQueue();
    } else if (activeTab === 'documents') {
      loadKnowledgeDocuments();
      loadKnowledgeStats();
    }
  }, [activeTab]);

  const handleFileUpload = async () => {
    if (!selectedFile || !uploadForm.title.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please select a PDF file and provide a title.",
      });
      return;
    }

    // Additional file validation
    if (!selectedFile.type.includes('pdf')) {
      toast({
        variant: "destructive",
        title: "Invalid File Type",
        description: "Please select a PDF file only.",
      });
      return;
    }

    // Check file size (50MB limit)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (selectedFile.size > maxSize) {
      toast({
        variant: "destructive",
        title: "File Too Large",
        description: `File size exceeds 50MB limit. Selected file is ${(selectedFile.size / (1024 * 1024)).toFixed(1)}MB.`,
      });
      return;
    }

    setUploadingDocument(true);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', uploadForm.title.trim());
      formData.append('description', uploadForm.description.trim());
      formData.append('category', uploadForm.category);
      formData.append('access_level', uploadForm.access_level);
      formData.append('tags', uploadForm.tags.trim());

      console.log('Uploading file:', selectedFile.name, 'Size:', selectedFile.size, 'Type:', selectedFile.type);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge_base/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Note: Do NOT set Content-Type header - let browser set it for FormData
        },
        body: formData
      });

      console.log('Upload response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('Upload successful:', result);
        
        toast({
          title: "Upload Successful",
          description: "PDF uploaded and processing started. Content will be indexed for AI features.",
        });

        // Reset form
        setSelectedFile(null);
        setUploadForm({
          title: '',
          description: '',
          category: 'educational',
          access_level: 'public',
          tags: ''
        });

        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
        }

        // Reload documents
        loadKnowledgeDocuments();
        loadKnowledgeStats();
      } else {
        let errorMessage = 'Upload failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        console.error('Upload failed:', response.status, errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Document upload error:', error);
      toast({
        variant: "destructive",
        title: "Upload Failed", 
        description: error.message || "Failed to upload document. Please try again.",
      });
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    const documentToDelete = knowledgeDocuments.find(doc => doc.id === documentId);
    
    if (!confirm(`Are you sure you want to delete "${documentToDelete?.title || 'this document'}"?\n\nThis action cannot be undone and will permanently remove the document from the knowledge base.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge_base/document/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast({
          title: "Document Deleted Successfully",
          description: `"${documentToDelete?.title}" has been permanently removed from the knowledge base.`,
        });
        
        // Close details modal if viewing deleted document
        if (selectedDocument?.id === documentId) {
          setSelectedDocument(null);
          setViewingDocumentDetails(false);
        }
        
        // Reload the documents list
        loadKnowledgeDocuments();
        loadKnowledgeStats();
      } else {
        let errorMessage = 'Failed to delete document';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Delete document error:', error);
      toast({
        variant: "destructive",
        title: "Delete Failed",
        description: error.message || "Failed to delete document. Please try again.",
      });
    }
  };

  const handleViewDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge_base/document/${documentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const documentDetails = await response.json();
        setSelectedDocument(documentDetails);
        setViewingDocumentDetails(true);
        
        toast({
          title: "Document Loaded",
          description: `Viewing details for "${documentDetails.title}".`,
        });
      } else {
        let errorMessage = 'Failed to load document details';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('View document error:', error);
      toast({
        variant: "destructive",
        title: "Failed to Load Document",
        description: error.message || "Could not load document details. Please try again.",
      });
    }
  };

  const handleDownloadDocument = async (documentId, fileName) => {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }

      // Show loading toast
      toast({
        title: "Starting Download",
        description: `Preparing "${fileName}" for download...`,
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/knowledge_base/document/${documentId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Get the blob data
        const blob = await response.blob();
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          title: "Download Complete",
          description: `"${fileName}" has been downloaded successfully.`,
        });
      } else {
        let errorMessage = 'Failed to download document';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Download document error:', error);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: error.message || "Failed to download document. Please try again.",
      });
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Admin Access Required</h2>
            <p className="text-gray-600">Please log in with admin credentials.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600">TribalRoot.AI Management Portal</p>
          </div>
          <div className="flex items-center space-x-4">
            <Badge variant="outline" className="bg-green-50 text-green-700">
              <Shield className="h-3 w-3 mr-1" />
              Admin Access
            </Badge>
            <Button variant="outline" size="sm">
              <Bell className="h-4 w-4 mr-2" />
              Notifications
            </Button>
          </div>
        </div>
      </div>

      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="transactions">Billing</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="ai-usage">AI Usage</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="support">Support</TabsTrigger>
            <TabsTrigger value="moderation">Moderation</TabsTrigger>
          </TabsList>

          {/* Dashboard Overview */}
          <TabsContent value="dashboard" className="space-y-6">
            {dashboardData && (
              <>
                {/* Top Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Total Users</p>
                          <p className="text-3xl font-bold text-gray-900">
                            {formatNumber(dashboardData.metrics.total_users)}
                          </p>
                        </div>
                        <Users className="h-8 w-8 text-blue-600" />
                      </div>
                      <div className="mt-2 text-sm text-green-600">
                        +{dashboardData.metrics.new_users_today} today
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                          <p className="text-3xl font-bold text-gray-900">
                            {formatCurrency(dashboardData.metrics.monthly_recurring_revenue)}
                          </p>
                        </div>
                        <DollarSign className="h-8 w-8 text-green-600" />
                      </div>
                      <div className="mt-2 text-sm text-green-600">
                        +12.5% vs last month
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">AI Prompts (24h)</p>
                          <p className="text-3xl font-bold text-gray-900">
                            {formatNumber(dashboardData.metrics.ai_prompts_24h)}
                          </p>
                        </div>
                        <Brain className="h-8 w-8 text-purple-600" />
                      </div>
                      <div className="mt-2 text-sm text-blue-600">
                        Processing active
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Open Tickets</p>
                          <p className="text-3xl font-bold text-gray-900">
                            {dashboardData.metrics.open_support_tickets}
                          </p>
                        </div>
                        <MessageSquare className="h-8 w-8 text-orange-600" />
                      </div>
                      <div className="mt-2 text-sm text-orange-600">
                        Needs attention
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Subscription Breakdown */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <PieChart className="h-5 w-5 mr-2" />
                        Active Subscriptions by Tier
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries(dashboardData.metrics.active_subscriptions).map(([tier, count]) => (
                          <div key={tier} className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-3 ${
                                tier === 'seed' ? 'bg-green-500' :
                                tier === 'root' ? 'bg-purple-500' :
                                tier === 'trunk' ? 'bg-blue-500' : 'bg-amber-500'
                              }`} />
                              <span className="capitalize font-medium">{tier} Plan</span>
                            </div>
                            <span className="font-bold">{count}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Activity className="h-5 w-5 mr-2" />
                        Recent Activity
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span>Documents generated today</span>
                          <Badge variant="secondary">{dashboardData.metrics.documents_generated_today}</Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span>Healing requests today</span>
                          <Badge variant="secondary">{dashboardData.metrics.healing_requests_today}</Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span>Language packs downloaded</span>
                          <Badge variant="secondary">{dashboardData.metrics.language_packs_downloaded}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Users & Tickets */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Users</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {dashboardData.recent_users.slice(0, 5).map((user) => (
                          <div key={user.id} className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">{user.full_name}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                            <Badge variant="outline" className="capitalize">
                              {user.subscription_tier}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Support Tickets</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {dashboardData.recent_tickets.slice(0, 5).map((ticket) => (
                          <div key={ticket.id} className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{ticket.subject}</p>
                              <p className="text-sm text-gray-500">{ticket.user_email}</p>
                            </div>
                            <Badge 
                              variant={ticket.priority === 'urgent' ? 'destructive' : 'secondary'}
                              className="ml-2"
                            >
                              {ticket.priority}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* User Management */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    User Management
                  </CardTitle>
                  <Button>
                    <Download className="h-4 w-4 mr-2" />
                    Export Users
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search and Filters */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1">
                    <Input
                      placeholder="Search by name, email, region..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <Select onValueChange={(value) => setUserFilters({...userFilters, tier: value})}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by tier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tiers</SelectItem>
                      <SelectItem value="seed">Seed</SelectItem>
                      <SelectItem value="root">Root</SelectItem>
                      <SelectItem value="trunk">Trunk</SelectItem>
                      <SelectItem value="crown">Crown</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={loadUsers}>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                </div>

                {/* Users Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">User</th>
                        <th className="text-left py-3 px-4">Subscription</th>
                        <th className="text-left py-3 px-4">Location</th>
                        <th className="text-left py-3 px-4">Activity</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b">
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium">{user.full_name}</p>
                              <p className="text-gray-500">{user.email}</p>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="outline" className="capitalize">
                              {user.subscription_tier}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">{user.location || 'Not specified'}</td>
                          <td className="py-3 px-4">
                            <div className="text-xs">
                              <div>{user.total_analyses} analyses</div>
                              <div>{user.total_crests} crests</div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant={user.is_active ? "default" : "destructive"}>
                              {user.is_active ? "Active" : "Suspended"}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex space-x-2">
                              <Button size="sm" variant="outline">
                                <Eye className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="outline">
                                <Edit className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tickets */}
          <TabsContent value="support" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Support Tickets
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4">Subject</th>
                        <th className="text-left py-3 px-4">User</th>
                        <th className="text-left py-3 px-4">Category</th>
                        <th className="text-left py-3 px-4">Priority</th>
                        <th className="text-left py-3 px-4">Status</th>
                        <th className="text-left py-3 px-4">Created</th>
                        <th className="text-left py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((ticket) => (
                        <tr key={ticket.id} className="border-b">
                          <td className="py-3 px-4 font-medium">{ticket.subject}</td>
                          <td className="py-3 px-4">{ticket.user_email}</td>
                          <td className="py-3 px-4">
                            <Badge variant="secondary">{ticket.category}</Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge 
                              variant={ticket.priority === 'urgent' ? 'destructive' : 
                                     ticket.priority === 'high' ? 'default' : 'secondary'}
                            >
                              {ticket.priority}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge 
                              variant={ticket.status === 'open' ? 'destructive' :
                                     ticket.status === 'resolved' ? 'default' : 'secondary'}
                            >
                              {ticket.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4">
                            <Button size="sm" variant="outline">
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Add other tab contents as needed */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Financial Dashboard</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Financial reports and transaction management coming soon...</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Knowledge Base Documents Tab */}
          <TabsContent value="documents" className="space-y-6">
            {/* Knowledge Base Stats */}
            {knowledgeStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Documents</p>
                        <p className="text-3xl font-bold text-gray-900">{knowledgeStats.total_documents}</p>
                      </div>
                      <FileText className="h-8 w-8 text-blue-600" />
                    </div>
                    <div className="mt-2 text-sm text-green-600">
                      {knowledgeStats.active_documents} active
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Processing</p>
                        <p className="text-3xl font-bold text-gray-900">{knowledgeStats.processing_documents}</p>
                      </div>
                      <Activity className="h-8 w-8 text-orange-600" />
                    </div>
                    <div className="mt-2 text-sm text-orange-600">
                      AI indexing
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Storage Used</p>
                        <p className="text-3xl font-bold text-gray-900">{knowledgeStats.total_size_mb}</p>
                      </div>
                      <Globe className="h-8 w-8 text-purple-600" />
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      MB total
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Categories</p>
                        <p className="text-3xl font-bold text-gray-900">{knowledgeStats.popular_categories?.length || 0}</p>
                      </div>
                      <BookOpen className="h-8 w-8 text-green-600" />
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      Active types
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* PDF Upload Section */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Upload className="h-5 w-5 mr-2" />
                      Upload Knowledge Document
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">PDF File *</label>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            // Validate file type
                            if (!file.type.includes('pdf')) {
                              toast({
                                variant: "destructive",
                                title: "Invalid File Type",
                                description: "Please select a PDF file only.",
                              });
                              e.target.value = ''; // Clear the input
                              setSelectedFile(null);
                              return;
                            }
                            
                            // Validate file size (50MB limit)
                            const maxSize = 50 * 1024 * 1024;
                            if (file.size > maxSize) {
                              toast({
                                variant: "destructive",
                                title: "File Too Large",
                                description: `File size exceeds 50MB limit. Selected file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`,
                              });
                              e.target.value = ''; // Clear the input  
                              setSelectedFile(null);
                              return;
                            }
                            
                            setSelectedFile(file);
                          }
                        }}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 rounded-md px-3 py-2"
                      />
                      {selectedFile && (
                        <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                          <p className="text-sm text-green-800">
                            ✅ Selected: {selectedFile.name}
                          </p>
                          <p className="text-xs text-green-600">
                            Size: {(selectedFile.size / (1024*1024)).toFixed(2)} MB • Type: {selectedFile.type}
                          </p>
                        </div>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Maximum file size: 50MB. Only PDF files are accepted.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Title *</label>
                      <Input
                        placeholder="Document title"
                        value={uploadForm.title}
                        onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <Textarea
                        placeholder="Brief description of the document"
                        rows={3}
                        value={uploadForm.description}
                        onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Category</label>
                      <Select 
                        value={uploadForm.category}
                        onValueChange={(value) => setUploadForm({...uploadForm, category: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="treaties">Treaties</SelectItem>
                          <SelectItem value="genealogy">Genealogy</SelectItem>
                          <SelectItem value="healing">Healing</SelectItem>
                          <SelectItem value="language">Language</SelectItem>
                          <SelectItem value="legal">Legal</SelectItem>
                          <SelectItem value="cultural">Cultural</SelectItem>
                          <SelectItem value="educational">Educational</SelectItem>
                          <SelectItem value="historical">Historical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Access Level</label>
                      <Select 
                        value={uploadForm.access_level}
                        onValueChange={(value) => setUploadForm({...uploadForm, access_level: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">Public</SelectItem>
                          <SelectItem value="authenticated">Authenticated Users</SelectItem>
                          <SelectItem value="premium">Premium Users</SelectItem>
                          <SelectItem value="admin_only">Admin Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Tags</label>
                      <Input
                        placeholder="Comma-separated tags (e.g., indigenous, legal, history)"
                        value={uploadForm.tags}
                        onChange={(e) => setUploadForm({...uploadForm, tags: e.target.value})}
                      />
                    </div>

                    <Button 
                      onClick={handleFileUpload}
                      disabled={uploadingDocument || !selectedFile || !uploadForm.title.trim()}
                      className="w-full"
                    >
                      {uploadingDocument ? (
                        <>
                          <Activity className="h-4 w-4 mr-2 animate-spin" />
                          Uploading & Processing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload PDF
                        </>
                      )}
                    </Button>

                    <div className="text-xs text-gray-500 space-y-1">
                      <p>• PDF files only, max 50MB</p>
                      <p>• Content will be AI-indexed for search</p>
                      <p>• Available for knowledge base features</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Documents List */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center">
                        <BookOpen className="h-5 w-5 mr-2" />
                        Knowledge Base Documents
                      </CardTitle>
                      <Button variant="outline" onClick={loadKnowledgeDocuments}>
                        <Download className="h-4 w-4 mr-2" />
                        Refresh
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Documents Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4">Document</th>
                            <th className="text-left py-3 px-4">Category</th>
                            <th className="text-left py-3 px-4">Access</th>
                            <th className="text-left py-3 px-4">Size</th>
                            <th className="text-left py-3 px-4">Status</th>
                            <th className="text-left py-3 px-4">Uploaded</th>
                            <th className="text-left py-3 px-4">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {knowledgeDocuments.map((doc) => (
                            <tr key={doc.id} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <div>
                                  <p className="font-medium">{doc.title}</p>
                                  <p className="text-gray-500 text-xs">{doc.file_name}</p>
                                  {doc.description && (
                                    <p className="text-gray-600 text-xs mt-1">{doc.description.substring(0, 100)}...</p>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant="outline" className="capitalize">
                                  {doc.category}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                <Badge variant="secondary" className="capitalize">
                                  {doc.access_level.replace('_', ' ')}
                                </Badge>
                              </td>
                              <td className="py-3 px-4">
                                {(doc.file_size / (1024*1024)).toFixed(2)} MB
                                {doc.page_count && (
                                  <div className="text-xs text-gray-500">{doc.page_count} pages</div>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <Badge 
                                  variant={
                                    doc.status === 'active' ? 'default' :
                                    doc.status === 'processing' ? 'secondary' : 'destructive'
                                  }
                                  className="capitalize"
                                >
                                  {doc.status}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-xs text-gray-500">
                                {new Date(doc.created_at).toLocaleDateString()}
                                <div className="text-xs">
                                  Views: {doc.view_count} | Downloads: {doc.download_count}
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex space-x-2">
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    title="View Details"
                                    onClick={() => handleViewDocument(doc.id)}
                                  >
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    title="Download"
                                    onClick={() => handleDownloadDocument(doc.id, doc.file_name)}
                                  >
                                    <Download className="h-3 w-3" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    title="Delete"
                                    onClick={() => handleDeleteDocument(doc.id)}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {knowledgeDocuments.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <BookOpen className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p>No documents uploaded yet.</p>
                          <p className="text-sm">Upload your first PDF to get started.</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Upload Troubleshooting Section */}
                <Card className="border-orange-200 bg-orange-50">
                  <CardHeader>
                    <CardTitle className="flex items-center text-orange-800">
                      <AlertTriangle className="h-5 w-5 mr-2" />
                      Upload Troubleshooting
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-orange-800 space-y-3">
                    <div>
                      <h4 className="font-medium mb-1">Common Upload Issues:</h4>
                      <ul className="space-y-1 text-xs list-disc list-inside">
                        <li>Make sure you're logged in as an admin user</li>
                        <li>Only PDF files are accepted (not images, Word docs, etc.)</li>
                        <li>File size must be under 50MB</li>
                        <li>Ensure stable internet connection during upload</li>
                        <li>Check browser console (F12) for detailed error messages</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium mb-1">If uploads still fail:</h4>
                      <ul className="space-y-1 text-xs list-disc list-inside">
                        <li>Try refreshing the page and logging in again</li>
                        <li>Clear browser cache and cookies</li>
                        <li>Try a different browser or incognito mode</li>
                        <li>Check if the PDF is corrupted by opening it elsewhere</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

        </Tabs>

        {/* Document Details Modal */}
        {viewingDocumentDetails && selectedDocument && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">{selectedDocument.title}</h2>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setViewingDocumentDetails(false);
                    setSelectedDocument(null);
                  }}
                >
                  ✕ Close
                </Button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Document Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Document Information</h3>
                      <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">File Name:</span>
                          <span>{selectedDocument.file_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">File Size:</span>
                          <span>{(selectedDocument.file_size / (1024*1024)).toFixed(2)} MB</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Category:</span>
                          <Badge variant="outline">{selectedDocument.category}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Access Level:</span>
                          <Badge variant={selectedDocument.access_level === 'public' ? 'default' : 'secondary'}>
                            {selectedDocument.access_level}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Status:</span>
                          <Badge variant={selectedDocument.status === 'processed' ? 'default' : 'secondary'}>
                            {selectedDocument.status}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Upload Details</h3>
                      <div className="bg-blue-50 p-4 rounded-lg space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">Uploaded:</span>
                          <span>{new Date(selectedDocument.created_at).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Last Modified:</span>
                          <span>{new Date(selectedDocument.updated_at).toLocaleString()}</span>
                        </div>
                        {selectedDocument.last_accessed && (
                          <div className="flex justify-between">
                            <span className="font-medium">Last Accessed:</span>
                            <span>{new Date(selectedDocument.last_accessed).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-700">
                          {selectedDocument.description || 'No description provided.'}
                        </p>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedDocument.tags && selectedDocument.tags.length > 0 ? (
                          selectedDocument.tags.map((tag, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">No tags assigned</span>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Statistics</h3>
                      <div className="bg-green-50 p-4 rounded-lg space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">Views:</span>
                          <span>{selectedDocument.view_count || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Downloads:</span>
                          <span>{selectedDocument.download_count || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">AI Analysis:</span>
                          <span>{selectedDocument.ai_analysis_complete ? '✅ Complete' : '⏳ Processing'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content Preview */}
                {selectedDocument.content_text && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Content Preview</h3>
                    <div className="bg-gray-50 p-4 rounded-lg max-h-64 overflow-y-auto">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {selectedDocument.content_text.substring(0, 1000)}
                        {selectedDocument.content_text.length > 1000 && '...'}
                      </p>
                    </div>
                  </div>
                )}

                {/* AI Analysis Results */}
                {selectedDocument.ai_analysis && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">AI Analysis Summary</h3>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700">
                        {selectedDocument.ai_analysis.substring(0, 500)}
                        {selectedDocument.ai_analysis.length > 500 && '...'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button 
                    onClick={() => handleDownloadDocument(selectedDocument.id, selectedDocument.file_name)}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download PDF
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={() => {
                      handleDeleteDocument(selectedDocument.id);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete Document
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;